import { useCallback, useState } from 'react';
import { useConfiguratorStore } from '@store/useConfiguratorStore';
import type { Logo } from '@/types';
import { removeBackground } from '@utils/backgroundRemoval';
import { detectFileFormat, processVectorFile } from '@utils/vectorConverter';
import { useToast } from '@components/UI/ToastProvider';
import './LogoUploader.css';

/** File extensions accepted by the uploader */
const ACCEPTED_EXTENSIONS = '.png,.jpg,.jpeg,.svg,.webp,.bmp,.gif,.eps,.ai';

/** Human-readable format list for UI */
const SUPPORTED_FORMATS_LABEL = 'SVG, PNG, JPG, EPS, or AI';

export default function LogoUploader() {
    const [isDragging, setIsDragging] = useState(false);
    const [removeBackgroundEnabled, setRemoveBackgroundEnabled] = useState(false);
    const [isConverting, setIsConverting] = useState(false);
    const { addLogo } = useConfiguratorStore();
    const { showToast } = useToast();

    const processFile = useCallback(async (file: File) => {
        const format = detectFileFormat(file);

        // Validate: must be a recognized format
        if (format === 'raster' && !file.type.startsWith('image/')) {
            showToast('Unsupported file format. Please upload ' + SUPPORTED_FORMATS_LABEL, 'error');
            return;
        }

        try {
            if (format === 'svg' || format === 'eps' || format === 'ai') {
                // --- Vector file path ---
                setIsConverting(true);

                const result = await processVectorFile(file);

                const logo: Logo = {
                    id: `logo-${Date.now()}`,
                    name: file.name,
                    originalImage: result.dataUrl,
                    processedImage: result.dataUrl, // Vector files don't need bg removal
                    width: result.width,
                    height: result.height,
                    uploadedAt: new Date(),
                    sourceType: result.sourceType,
                    svgContent: result.svgContent,
                    sourceFile: result.sourceFile,
                    sourceFileName: result.sourceFileName,
                };

                addLogo(logo);
                showToast('Logo uploaded successfully!', 'success');
                setIsConverting(false);

            } else {
                // --- Raster file path (existing logic) ---
                const reader = new FileReader();
                reader.onload = async (e) => {
                    const originalImage = e.target?.result as string;

                    const img = new Image();
                    img.onload = async () => {
                        let processedImage = originalImage;

                        if (removeBackgroundEnabled) {
                            processedImage = await removeBackground(originalImage);
                        }

                        const logo: Logo = {
                            id: `logo-${Date.now()}`,
                            name: file.name,
                            originalImage,
                            processedImage,
                            width: img.width,
                            height: img.height,
                            uploadedAt: new Date(),
                            sourceType: 'raster',
                            sourceFileName: file.name,
                        };

                        addLogo(logo);
                        showToast('Logo uploaded successfully!', 'success');
                    };
                    img.src = originalImage;
                };
                reader.readAsDataURL(file);
            }
        } catch (error) {
            setIsConverting(false);
            const message = error instanceof Error ? error.message : 'Unknown error';
            console.error('File processing error:', error);
            showToast(`Upload failed: ${message}`, 'error');
        }
    }, [addLogo, removeBackgroundEnabled, showToast]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        files.forEach(processFile);
    }, [processFile]);

    const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        files.forEach(processFile);
        // Reset input value to allow re-uploading the same file
        e.target.value = '';
    }, [processFile]);

    return (
        <div className="logo-uploader-container">
            <div 
                className={`upload-input-box ${isDragging ? 'dragover' : ''} ${isConverting ? 'converting' : ''}`}
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
            >
                <input
                    type="file"
                    id="logo-upload"
                    accept={ACCEPTED_EXTENSIONS}
                    multiple
                    onChange={handleFileInput}
                    style={{ display: 'none' }}
                />
                <span className="upload-placeholder">Select or drag & drop files</span>
                <label htmlFor="logo-upload" className="upload-btn-inside">
                    Upload
                </label>
            </div>
            
            <p className="upload-helper-text">
                Drag & Drop or <label htmlFor="logo-upload" className="text-link">Choose file</label> to upload {SUPPORTED_FORMATS_LABEL}
            </p>

            {/* Preserving functionality: Background Removal Toggle */}
            <div className="upload-options-compact">
                <label className="bg-remove-toggle">
                    <input 
                        type="checkbox" 
                        id="bg-remove-checkbox"
                        aria-label="Remove background"
                        checked={removeBackgroundEnabled}
                        onChange={(e) => setRemoveBackgroundEnabled(e.target.checked)}
                    />
                    <span>Remove background</span>
                </label>
            </div>
        </div>
    );
}
